import { Box, FormControl, FormGroup, FormControlLabel, Checkbox, Typography, TextField, Radio, RadioGroup, Button } from '@mui/material';
import { Snackbar, Alert } from '@mui/material';
import { ListingOnboardingStepProps, ListingFormData } from '../../types/listing_onboarding'
import { InputAdornment } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../styles/theme'
import GooglePlacesAutocomplete from '../../components/GooglePlacesAutoComplete';
import { useEffect, useState, useMemo } from 'react';
import { format } from 'date-fns';
import CustomDatePicker from '../../components/CustomDatePicker';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const ListingOnboardingStep0: React.FC<ListingOnboardingStepProps> = ({ formData, onNext, setFormData, setStepValid }) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

    // Memoize date value to prevent DatePicker from resetting
    const memoizedDateValue = useMemo(() => {
        return formData.date_available 
            ? new Date(`${formData.date_available}T00:00:00`) 
            : null;
    }, [formData.date_available]);

    // Move validation logic into a function that runs on form data changes
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};


        // Only show rent error if field has been touched
        if (touched.rent) {
            if (!formData.rent || formData.rent.trim() === '') {
                newErrors.rent = 'Rent is required.';
            } else if (isNaN(Number(formData.rent.trim()))) {
                newErrors.rent = 'Rent must be a number.';
            } else if (Number(formData.rent.trim()) < 0) {
                newErrors.rent = 'Rent must be a positive number.';
            } else {
            }
        }

        // Only show address error if field has been touched
        if (touched.full_address) {
            if (!formData.full_address || formData.full_address.trim() === '') {
                newErrors.full_address = 'Address is required.';
            }
        }

        if (!formData.property_type) {
            newErrors.property_type = 'Property type is required.';
        }

        if (!formData.date_available) {
            newErrors.date_available = 'Date available is required.';
        }

        // Only show lease type error if lease types section has been touched
        if (touched.lease_types) {
            const hasSelectedLeaseType = (
                formData.lease_types.month_to_month ||
                formData.lease_types.sublease ||
                formData.lease_types.fixed_term_lease
            );

            if (!hasSelectedLeaseType) {
                newErrors.lease_types = 'At least one lease type must be selected.';
            }
        }

        // Only show pet policy error if pet policy section has been touched
        if (touched.pet_policy) {
            const hasValidPetPolicy = (
                formData.pet_policy.no_pets_allowed ||
                formData.pet_policy.cats_allowed ||
                formData.pet_policy.small_dogs_allowed ||
                formData.pet_policy.large_dogs_allowed
            );

            if (!hasValidPetPolicy) {
                newErrors.pet_policy = 'At least one pet policy option must be selected.';
            }
        }

        setErrors(newErrors);
    };

    // Run validation whenever form data or touched state changes
    useEffect(() => {
        validateForm();
    }, [formData, touched]);

    useEffect(() => {
        const hasSelectedLeaseType = (
            formData.lease_types.month_to_month ||
            formData.lease_types.sublease ||
            formData.lease_types.fixed_term_lease
        );

        const hasValidPetPolicy = (
            formData.pet_policy.no_pets_allowed ||
            formData.pet_policy.cats_allowed ||
            formData.pet_policy.small_dogs_allowed ||
            formData.pet_policy.large_dogs_allowed
        );

        const isValid = Boolean(
            formData.full_address &&
            formData.rent &&
            formData.rent.trim() !== '' &&
            formData.rental_type &&
            formData.property_type &&
            formData.date_available &&
            formData.laundry_type &&
            hasSelectedLeaseType &&
            hasValidPetPolicy
        );

        setStepValid(isValid);
    }, [
        formData.full_address,
        formData.rent,
        formData.female_count,
        formData.male_count,
        formData.rental_type,
        formData.property_type,
        formData.date_available,
        formData.laundry_type,
        formData.lease_types.month_to_month,
        formData.lease_types.sublease,
        formData.lease_types.fixed_term_lease,
        formData.pet_policy.no_pets_allowed,
        formData.pet_policy.cats_allowed,
        formData.pet_policy.small_dogs_allowed,
        formData.pet_policy.large_dogs_allowed,
        setStepValid
    ]);

    const handlePetPolicyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Mark pet policy as touched when user interacts with any pet policy checkbox
        setTouched(prev => ({ ...prev, pet_policy: true }));

        const { name, checked } = event.target;

        // If "no pets allowed" is being checked, uncheck all other options
        if (name === 'no_pets_allowed' && checked) {
            setFormData(prev => ({
                ...prev,
                pet_policy: {
                    no_pets_allowed: true,
                    cats_allowed: false,
                    small_dogs_allowed: false,
                    large_dogs_allowed: false,
                }
            }));
            return;
        }

        // If any pet option is being checked, uncheck "no pets allowed"
        const updatedPetPolicy = {
            ...formData.pet_policy,
            [name]: checked,
        };

        if (name !== 'no_pets_allowed' && checked) {
            updatedPetPolicy.no_pets_allowed = false;
        }

        setFormData(prev => ({
            ...prev,
            pet_policy: updatedPetPolicy
        }));
    };

    const handleLaundryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedData: ListingFormData = {
            ...formData,
            laundry_type: event.target.value
        };
        setFormData(updatedData)
    }

    const handleLeaseTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Mark lease types as touched when user interacts with any lease type checkbox
        setTouched(prev => ({ ...prev, lease_types: true }));

        const updatedData: ListingFormData = {
            ...formData,
            lease_types: {
                ...formData.lease_types,
                [event.target.name]: event.target.checked
            }
        };
        setFormData(updatedData);
    };

    const handleBedroomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedData: ListingFormData = {
            ...formData,
            num_bedrooms: event.target.value
        };
        setFormData(updatedData)
    }

    const handleBathroomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedData: ListingFormData = {
            ...formData,
            num_bathrooms: event.target.value
        };
        setFormData(updatedData)
    }

    const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPrice = event.target.value.toString().trim();
        
        // Update form data
        setFormData(prevData => ({
            ...prevData,
            rent: newPrice,
            security_deposit: newPrice
        }));

        // Validate the input immediately if field has been touched
        if (touched.rent) {
            const newErrors = { ...errors };
            
            if (!newPrice || newPrice === '') {
                newErrors.rent = 'Rent is required.';
            } else if (isNaN(Number(newPrice))) {
                newErrors.rent = 'Rent must be a number.';
            } else if (Number(newPrice) < 0) {
                newErrors.rent = 'Rent must be a positive number.';
            } else {
                delete newErrors.rent; // Clear the error if validation passes
            }
            
            setErrors(newErrors);
        }
    };

    const handlePriceBlur = () => {
        // Mark field as touched when user leaves the field
        setTouched(prev => ({ ...prev, rent: true }));
    };

    const handleAddressChange = async (
        addressOrEvent: string | React.ChangeEvent<HTMLInputElement>,
        placeId?: string
    ) => {
        const full_address = typeof addressOrEvent === 'string'
            ? addressOrEvent
            : addressOrEvent.target.value;

        // Create a new form data object with the updated address
        const updatedFormData = {
            ...formData,
            full_address: full_address
        };
        // If a place ID was provided, also update that field
        if (placeId) {
            updatedFormData.google_place_id = placeId;
        }

        // Update the form data with our changes
        setFormData(updatedFormData);
    };

    const handleAddressBlur = () => {
        // Mark field as touched when user leaves the field
        setTouched(prev => ({ ...prev, full_address: true }));
    };

    return (
        <ThemeProvider theme={theme}>
            <Snackbar open={isGenerating} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} onClose={() => setIsGenerating(false)} autoHideDuration={10000}>
                <Alert severity="info" variant="filled" sx={{
                    zIndex: 2000, width: '100%', backgroundColor: 'black', color: 'white',
                    '& .MuiAlert-icon': {
                        color: 'white'
                    }
                }}
                >
                    We are creating your listing description from online data, it will be visible on the next screen ✨️
                </Alert>
            </Snackbar>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '0 0 10 0' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 500 }}>
                    Get your listing live.
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: '#ff4444',
                        opacity: 0.8,
                        mb: 2,
                        fontStyle: 'italic'
                    }}
                >
                </Typography>

                <FormControl component="fieldset" sx={{ mt: 2, width: '90%' }}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '1.5rem', mb: '0.8rem' }} gutterBottom>
                        How much is rent?
                    </Typography>
                    <TextField
                        id="rent"
                        name="rent"
                        variant="outlined"
                        type="text"
                        value={formData.rent}
                        onChange={handlePriceChange}
                        onBlur={handlePriceBlur}
                        error={!!errors.rent}
                        helperText={errors.rent}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        FormHelperTextProps={{
                            sx: {
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.75rem',
                            }
                        }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        Date Available
                    </Typography>
                    <CustomDatePicker
                        value={memoizedDateValue}
                        onChange={(newValue) => {
                            const formatted = newValue ? format(newValue, 'yyyy-MM-dd') : '';
                            setFormData({ ...formData, date_available: formatted });
                        }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        Enter property address
                    </Typography>
                    <GooglePlacesAutocomplete
                        value={formData.full_address}
                        onChange={(full_address, placeId) => {
                            handleAddressChange({
                                target: { value: full_address }
                            } as React.ChangeEvent<HTMLInputElement>, placeId);
                        }}
                        textFieldProps={{
                            error: !!errors.full_address,
                            helperText: errors.full_address,
                            onBlur: handleAddressBlur,
                            FormHelperTextProps: {
                                sx: {
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '0.75rem',
                                }
                            }
                        }}
                    />
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        Property Type
                    </Typography>
                    <RadioGroup
                        name="property_type"
                        value={formData.property_type || ''}
                        onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                    >
                        <FormControlLabel value="house" control={<Radio />} label="House" />
                        <FormControlLabel value="town_house" control={<Radio />} label="Townhouse" />
                        <FormControlLabel value="apartment" control={<Radio />} label="Apartment" />

                    </RadioGroup>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        Type of Rental
                    </Typography>
                    <RadioGroup
                        name="rental_type"
                        value={formData.rental_type || ''}
                        onChange={(e) => {
                            const rentalType = e.target.value;
                            setFormData(prev => ({
                                ...prev,
                                rental_type: rentalType,
                                // Reset roommate counts when switching to entire place
                                male_count: rentalType === 'entire_place' ? undefined : prev.male_count,
                                female_count: rentalType === 'entire_place' ? undefined : prev.female_count,
                                unit_occupied: rentalType === 'private_room'
                                ,
                            }));
                        }}
                    >
                        <FormControlLabel
                            value="entire_place"
                            control={<Radio sx={{
                                color: 'grey.500',
                                '&.Mui-checked': {
                                    color: 'black',
                                },
                            }} />}
                            label={
                                <Box>
                                    <Typography variant="body1">
                                        Entire Place
                                    </Typography>
                                </Box>
                            }
                        />
                        <FormControlLabel
                            value="private_room"
                            control={<Radio sx={{
                                color: 'grey.500',
                                '&.Mui-checked': {
                                    color: 'black',
                                },
                            }} />}
                            label={
                                <Box>
                                    <Typography variant="body1" >
                                        Private Room
                                    </Typography>
                                </Box>
                            }
                        />
                    </RadioGroup>
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        # of bedrooms
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            onClick={() => handleBedroomChange({
                                target: { value: String(Number(formData.num_bedrooms) - 1) }
                            } as React.ChangeEvent<HTMLInputElement>)}
                            disabled={Number(formData.num_bedrooms) <= 0}
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                p: 0,
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                color: 'black',
                                mx: 2
                            }}
                        >
                            <RemoveIcon />
                        </Button>
                        <TextField
                            id="bedrooms"
                            name="bedrooms"
                            variant="outlined"
                            type="number"
                            value={formData.num_bedrooms}
                            onChange={handleBedroomChange}
                            sx={{
                                width: '70px',
                                '& input': { textAlign: 'center' }
                            }}
                            InputProps={{
                                inputProps: { min: 0 },
                            }}
                        />
                        <Button
                            onClick={() => handleBedroomChange({
                                target: { value: String(Number(formData.num_bedrooms) + 1) }
                            } as React.ChangeEvent<HTMLInputElement>)}
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                p: 0,
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                color: 'black',
                                mx: 2
                            }}
                        >
                            <AddIcon />
                        </Button>
                    </Box>

                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        # of bathrooms
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            onClick={() => handleBathroomChange({
                                target: { value: String(Number(formData.num_bathrooms) - 1) }
                            } as React.ChangeEvent<HTMLInputElement>)}
                            disabled={(Number(formData.num_bathrooms)) <= 0}
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                p: 0,
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                color: 'black',
                                mx: 2
                            }}
                        >
                            <RemoveIcon />
                        </Button>
                        <TextField
                            id="bathrooms"
                            name="bathrooms"
                            variant="outlined"
                            type="number"
                            value={formData.num_bathrooms}
                            onChange={handleBathroomChange}
                            sx={{
                                width: '70px',
                                '& input': { textAlign: 'center' }
                            }}
                            InputProps={{
                                inputProps: { min: 0 },
                            }}
                        />
                        <Button
                            onClick={() => handleBathroomChange({
                                target: { value: String(Number(formData.num_bathrooms) + 1) }
                            } as React.ChangeEvent<HTMLInputElement>)}
                            sx={{
                                minWidth: '40px',
                                height: '40px',
                                p: 0,
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                color: 'black',
                                mx: 2
                            }}
                        >
                            <AddIcon />
                        </Button>
                    </Box>
                    {/* Conditional fields for Private Room */}
                    {formData.rental_type === "private_room" && (
                        <>
                            {/* Female Roommates */}
                            <Typography variant="body1" sx={{ mt: '2rem', mb: '0.8rem', fontWeight: 'bold' }}># of Female Roommates</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        female_count: String(Math.max(0, Number(prev.female_count || 0) - 1))
                                    }))}
                                    disabled={!formData.female_count || Number(formData.female_count) <= 0}
                                    sx={{
                                        minWidth: '40px',
                                        height: '40px',
                                        p: 0,
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        color: 'black',
                                        mx: 2
                                    }}
                                >
                                    <RemoveIcon />
                                </Button>
                                <TextField
                                    id="female_count"
                                    type="number"
                                    value={formData.female_count || '0'}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        female_count: e.target.value
                                    }))}
                                    sx={{
                                        width: '70px',
                                        '& input': { textAlign: 'center' }
                                    }}
                                    InputProps={{
                                        inputProps: { min: 0 }
                                    }}
                                />
                                <Button
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        female_count: String(Number(prev.female_count || 0) + 1)
                                    }))}
                                    sx={{
                                        minWidth: '40px',
                                        height: '40px',
                                        p: 0,
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        color: 'black',
                                        mx: 2
                                    }}
                                >
                                    <AddIcon />
                                </Button>
                            </Box>

                            {/* Male Roommates */}
                            <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }}># of Male Roommates</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        male_count: String(Math.max(0, Number(prev.male_count || 0) - 1))
                                    }))}
                                    disabled={!formData.male_count || Number(formData.male_count) <= 0}
                                    sx={{
                                        minWidth: '40px',
                                        height: '40px',
                                        p: 0,
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        color: 'black',
                                        mx: 2
                                    }}
                                >
                                    <RemoveIcon />
                                </Button>
                                <TextField
                                    id="male_count"
                                    type="number"
                                    value={formData.male_count || '0'}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        male_count: e.target.value
                                    }))}
                                    sx={{
                                        width: '70px',
                                        '& input': { textAlign: 'center' }
                                    }}
                                    InputProps={{
                                        inputProps: { min: 0 }
                                    }}
                                />
                                <Button
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        male_count: String(Number(prev.male_count || 0) + 1)
                                    }))}
                                    sx={{
                                        minWidth: '40px',
                                        height: '40px',
                                        p: 0,
                                        border: '1px solid rgba(0, 0, 0, 0.23)',
                                        color: 'black',
                                        mx: 2
                                    }}
                                >
                                    <AddIcon />
                                </Button>
                            </Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                                Is the Bathroom Private?
                            </Typography>
                            <RadioGroup
                                name="has_private_bathroom"
                                value={formData.has_private_bathroom?.toString() || 'true'}
                                onChange={(e) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        has_private_bathroom: e.target.value === 'true'
                                    }));
                                }}
                            >
                                <FormControlLabel
                                    value="true"
                                    control={<Radio sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }} />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                Yes, tenant will have their own bathroom
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <FormControlLabel
                                    value="false"
                                    control={<Radio sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }} />}
                                    label={
                                        <Box>
                                            <Typography variant="body1">
                                                No, tenant will share the bathroom
                                            </Typography>
                                        </Box>
                                    }
                                />
                            </RadioGroup>
                            {formData.has_private_bathroom === false && (
                                <>
                                    <Typography variant="body1" sx={{ mt: '2rem', mb: '0.8rem' }} gutterBottom>
                                        Total # of people using the bathroom
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Button
                                            onClick={() => {
                                                const currentValue = Number(formData.shared_bathroom_count || 2);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    shared_bathroom_count: String(Math.max(2, currentValue - 1))
                                                }));
                                            }}
                                            disabled={Number(formData.shared_bathroom_count || 2) <= 2}
                                            sx={{
                                                minWidth: '40px',
                                                height: '40px',
                                                p: 0,
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                color: 'black',
                                                mx: 1
                                            }}
                                        >
                                            <RemoveIcon />
                                        </Button>
                                        <TextField
                                            value={formData.shared_bathroom_count || 2}
                                            onChange={(e) => {
                                                setFormData(prev => ({
                                                    ...prev,
                                                    shared_bathroom_count: e.target.value
                                                }));
                                            }}
                                            sx={{
                                                width: '70px',
                                                '& input': { textAlign: 'center' },
                                                mx: 1
                                            }}
                                            InputProps={{
                                                inputProps: { min: 2 },
                                            }}
                                        />
                                        <Button
                                            onClick={() => {
                                                const currentValue = Number(formData.shared_bathroom_count || 2);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    shared_bathroom_count: String(currentValue + 1)
                                                }));
                                            }}
                                            sx={{
                                                minWidth: '40px',
                                                height: '40px',
                                                p: 0,
                                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                                color: 'black',
                                                mx: 1
                                            }}
                                        >
                                            <AddIcon />
                                        </Button>
                                    </Box>
                                </>
                            )}
                        </>
                    )}

                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        Available lease types
                    </Typography>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.lease_types.fixed_term_lease}
                                    name="fixed_term_lease"
                                    onChange={handleLeaseTypeChange}
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label="Fixed Term Lease (12 months)"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.lease_types.month_to_month}
                                    name="month_to_month"
                                    onChange={handleLeaseTypeChange}
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label="Month to Month"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.lease_types.sublease}
                                    onChange={handleLeaseTypeChange}
                                    name="sublease"
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label="Sublease"
                        />
                    </FormGroup>
                    {errors.lease_types && (
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'error.main', 
                                mb: 1,
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.75rem',
                            }}
                        >
                            {errors.lease_types}
                        
                        </Typography>
                    )}
                    
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        What's your pet policy?
                    </Typography>

                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.pet_policy.no_pets_allowed}
                                    onChange={handlePetPolicyChange}
                                    name="no_pets_allowed"
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label="No Pets Allowed"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.pet_policy.cats_allowed}
                                    onChange={handlePetPolicyChange}
                                    name="cats_allowed"
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label="Cats Allowed"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.pet_policy.small_dogs_allowed}
                                    onChange={handlePetPolicyChange}
                                    name="small_dogs_allowed"
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label="Small Dogs Allowed"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.pet_policy.large_dogs_allowed}
                                    onChange={handlePetPolicyChange}
                                    name="large_dogs_allowed"
                                    sx={{
                                        color: 'grey.500',
                                        '&.Mui-checked': {
                                            color: 'black',
                                        },
                                    }}
                                />
                            }
                            label="Large Dogs Allowed"
                        />
                    </FormGroup>
                    {errors.pet_policy && (
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'error.main', 
                                mb: 1,
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.75rem',
                            }}
                        >
                            {errors.pet_policy}
                        </Typography>
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mt: '2rem', mb: '0.8rem' }} gutterBottom>
                        Laundry
                    </Typography>

                    <RadioGroup
                        name="laundry"
                        value={formData.laundry_type || ''}
                        onChange={handleLaundryChange}
                    >
                        <FormControlLabel value="in_unit" control={<Radio />} label="In Unit" />
                        <FormControlLabel value="in_building" control={<Radio />} label="In Building" />
                        <FormControlLabel value="no_laundry" control={<Radio />} label="None" />
                    </RadioGroup>
                </FormControl>
            </Box>
        </ThemeProvider>
    );
};

export default ListingOnboardingStep0;